<!-- Copyright 2022, Common Good Learning Tools LLC -->
<template><div class="d-flex justify-center align-stretch">
	<div class="k-main-collection">
		<div v-if="!lti_chooser" class="d-flex align-center mb-5">
			<k-icon color="primary" class="k-collection-header-icon mr-3">{{collection_icon}}</k-icon>
			<h1 class="k-page-title mb-2"><b v-html="page_title"></b></h1>

			<k-spacer/>

			<k-btn v-if="!site_config.use_vx_nav && signed_in && !reorder_toggle" color="secondary" class="k-tight-btn k-nocaps-btn" @click="go_to_favorites"><k-icon small class="mr-2">fas fa-diagram-project</k-icon>Favorite Collections</k-btn>

			<k-btn v-if="signed_in && (user_is_superuser || show_subdomain_edit_tools)" color="secondary" class="ml-2 k-tight-btn k-nocaps-btn" @click="reorder_toggle=!reorder_toggle"><k-icon small class="mr-2">fas fa-up-down-left-right</k-icon>{{reorder_toggle ? 'Stop Editing Categories' : 'Edit Collection Categories'}}</k-btn>
		</div>

		<div v-if="initialized&&custom_tab_content_description&&!lti_chooser" class="k-custom-tab-content-description" :style="site_config.customized_tabs.resourcerepos.description_style" v-html="custom_tab_content_description"></div>

		<div v-show="initialized">
			<!-- this is unlikely to happen... -->
			<div v-if="collection_lps.length==0">There are currently no collections available.</div>
			<div v-if="!lti_chooser" class="d-flex mb-4 align-center">
				<k-spacer/>
				<div v-if="collection_lps.length>0" style="width: 240px"><k-text-field v-model="search_string" prepend-inner-icon="fa fa-search" prepend-decorative aria-label="Search collection titles" clearable clear-icon="fa fa-times-circle" label="Search collection titles…" single-line hide-details outlined dense background-color="#fff"></k-text-field></div>
				<k-btn outlined class="k-search-initialize-btn ml-2" style="width: 240px" @click="initiate_search">Search site content<div class="k-search-initialize-btn-icon-div"><k-icon small>fas fa-search</k-icon></div></k-btn>
				<k-spacer/>
			</div>

			<!-- if we're editing favorites... -->
			<div v-if="reorder_toggle">
				<div
					v-if="show_subdomain_category"
					class="k-repo-category k-subdomain-repo-category pt-14"
					style="min-width: 360px"
					role="group"
					:aria-labelledby="repo_category_heading_id('subdomain')"
				>
					<div class="k-repo-category-label" :id="repo_category_heading_id('subdomain')">
						<div style="font-weight: normal; margin-bottom: 2px;">Agency Repositories:</div>
						{{subdomain_data.agency_name}}
					</div>
					<div v-if="subdomain_repos.length>0">
						<draggable v-bind="drag_options" class="d-flex flex-wrap justify-center" group="subdomain_repos" data-list-name="subdomain_repos" :list="subdomain_repos" @change="repo_order_changed('subdomain_repos', subdomain_repos, $event)">
							<div
								v-for="(lp, index) in subdomain_repos"
								:key="'subdomain_repo_' + index"
								class="k-elevated k-main-collection-item text-left k-lp-tile"
								:class="lp_css(lp)"
								:style="[lp_style(lp), {'color': U.get_contrast_color(lp.color)}]"
							>
								<span>
									<k-icon small style="font-size:14px; margin-right:4px;" :style="`color:${U.get_contrast_color(lp.color)}`">{{lp.collection_icon()}}</k-icon><span v-html="lp_title(lp)"></span>
								</span>
								<div style="position: absolute; right: 12px; bottom: 12px; font-size: 14px;">
									<k-icon>fas fa-up-down-left-right</k-icon>
								</div>
							</div>
						</draggable>
					</div>
				</div>

				<!-- note that `draggable=".k-repo-category-draggable"` makes it so that the default categories can't be dragged; but lps in the default categories can be dragged -->
				<draggable v-if="user_is_superuser" v-bind="drag_options" v-model="category_order" @change="category_order_changed" draggable=".k-repo-category-draggable" class="d-flex flex-wrap justify-center">
					<div v-for="(name, index) in category_order" :key="'category_order_' + index" class="k-repo-category k-repo-category-draggable" v-if="name in collection_categories">
						<div class="k-repo-category-label">
							<k-icon style="margin-top: -4px; font-size: 18px;" class="mr-2">fas fa-up-down-left-right</k-icon>
							{{ collection_categories[name].display_cat_name }}
							<k-btn icon x-small class="ml-2" :accessible-label="`Edit category title for ${collection_categories[name].display_cat_name}`" @click="edit_category_title(collection_categories[name])"><k-icon style="font-size: 18px;">fas fa-edit</k-icon></k-btn>
						</div>
						<draggable v-bind="drag_options" class="d-flex flex-wrap justify-center" group="categories" :data-list-name="name" :list="collection_categories[name].lps" @change="repo_order_changed(name, collection_categories[name].lps), $event">
							<div
								v-for="(lp, index) in collection_categories[name].lps"
								:key="'collection_category' + index"
								class="k-elevated k-main-collection-item text-left k-lp-tile"
								:class="lp_css(lp)"
								:style="[lp_style(lp), {'color': U.get_contrast_color(lp.color)}]"
							>
								<span>
									<k-icon small style="font-size:14px; margin-right:4px;" :style="`color:${U.get_contrast_color(lp.color)}`">{{lp.collection_icon()}}</k-icon><span v-html="lp_title(lp)"></span>
								</span>
								<div style="position: absolute; right: 12px; bottom: 12px; font-size: 14px;">
									<k-btn icon x-small :accessible-label="`Edit category title for ${lp.title}`" @click="edit_lp_category_title(lp)"><k-icon>fas fa-edit</k-icon></k-btn>
									<k-icon>fas fa-up-down-left-right</k-icon>
								</div>
							</div>
						</draggable>
					</div>
					<div v-if="default_repos.default_pd_repo_cat.lps.length > 0" key="default_pd" class="k-repo-category">
						<div class="k-repo-category-label" v-html="default_repos.default_pd_repo_cat.display_cat_name"></div>
						<draggable v-bind="drag_options" class="d-flex flex-wrap justify-center" group="categories" data-list-name="default_pd" :list="default_repos.default_pd_repo_cat.lps" @change="repo_order_changed('default_pd', default_repos.default_pd_repo_cat.lps), $event">
							<div
								v-for="(lp, index) in default_repos.default_pd_repo_cat.lps"
								:key="'default_pd' + index"
								class="k-elevated k-main-collection-item text-left k-lp-tile"
								:class="lp_css(lp)"
								:style="[lp_style(lp), {'color': U.get_contrast_color(lp.color)}]"
							>
								<span>
									<k-icon small style="font-size:14px; margin-right:4px;" :style="`color:${U.get_contrast_color(lp.color)}`">{{lp.collection_icon()}}</k-icon><span v-html="lp_title(lp)"></span>
								</span>
								<div style="position: absolute; right: 12px; bottom: 12px; font-size: 14px;">
									<k-btn icon x-small :accessible-label="`Edit category title for ${lp.title}`" @click="edit_lp_category_title(lp)"><k-icon>fas fa-edit</k-icon></k-btn>
									<k-icon>fas fa-up-down-left-right</k-icon>
								</div>
							</div>
						</draggable>
					</div>
					<div v-if="default_repos.default_repo_cat.lps.length > 0" key="default_repo" class="k-repo-category">
						<div class="k-repo-category-label" v-html="default_repos.default_repo_cat.display_cat_name"></div>
						<draggable v-bind="drag_options" class="d-flex flex-wrap justify-center" group="categories" data-list-name="default_repo" :list="default_repos.default_repo_cat.lps" @change="repo_order_changed('default_repo', default_repos.default_repo_cat.lps, $event)">
							<div
								v-for="(lp, index) in default_repos.default_repo_cat.lps"
								:key="'default_repo' + index"
								class="k-elevated k-main-collection-item text-left k-lp-tile"
								:class="lp_css(lp)"
								:style="[lp_style(lp), {'color': U.get_contrast_color(lp.color)}]"
							>
								<span>
									<k-icon small style="font-size:14px; margin-right:4px;" :style="`color:${U.get_contrast_color(lp.color)}`">{{lp.collection_icon()}}</k-icon><span v-html="lp_title(lp)"></span>
								</span>
								<div style="position: absolute; right: 12px; bottom: 12px; font-size: 14px;">
									<k-btn icon x-small :accessible-label="`Edit category title for ${lp.title}`" @click="edit_lp_category_title(lp)"><k-icon>fas fa-edit</k-icon></k-btn>
									<k-icon>fas fa-up-down-left-right</k-icon>
								</div>
							</div>
						</draggable>
					</div>
				</draggable>
			</div>

			<!-- if we're not editing favorites... -->
			<div v-else class="d-flex flex-wrap justify-center">
				<div v-if="show_subdomain_category" class="k-repo-category k-subdomain-repo-category pt-14" style="min-width: 360px">
					<div class="k-repo-category-label">
						<div style="font-weight: normal; margin-bottom: 2px;">Agency Repositories: </div>
						{{subdomain_data.agency_name}}
					</div>
					<div v-if="subdomain_repos.length>0" class="d-flex">
						<div
							v-for="(lp, index) in subdomain_repos"
							:key="'subdomain_repo_' + index"
							class="k-elevated k-main-collection-item text-left k-lp-tile k-repo-tile-button"
							:class="lp_css(lp)"
							:style="[lp_style(lp), {'color': U.get_contrast_color(lp.color)}]"
							:aria-label="`Open ${lp.title}`"
							role="button"
							tabindex="0"
							@click="go_to_lp(lp)"
							@keydown="repo_tile_keydown($event, lp)"
						>
							<span>
								<k-icon small style="font-size:14px; margin-right:4px;" :style="`color:${U.get_contrast_color(lp.color)}`">{{lp.collection_icon()}}</k-icon><span v-html="lp_title(lp)"></span>
							</span>
						</div>
					</div>
					<div v-if="show_subdomain_edit_tools" class="my-2 text-center" style="flex-basis: 100%">
						<k-btn class="k-tight-btn" color="pink darken-3" dark @click="create_subdomain_repo"><k-icon small class="mr-2">fas fa-plus</k-icon> Create a new Agency Repository</k-btn>
					</div>
				</div>

				<div
					v-for="(name, index) in category_order"
					:key="'category_order_' + index"
					class="k-repo-category"
					role="group"
					:aria-labelledby="repo_category_heading_id(name)"
				>
					<template v-if="name in collection_categories">
						<div class="k-repo-category-label" :id="repo_category_heading_id(name)" v-html="collection_categories[name].display_cat_name"></div>
						<div
							v-for="(lp, index) in collection_categories[name].lps"
							:key="name + '_lp_' + index"
							v-show="collection_lps_to_show.find(x=>x.lp_id==lp.lp_id)"
							class="k-elevated k-main-collection-item text-left k-lp-tile k-repo-tile-button"
							:class="lp_css(lp)"
							:style="[lp_style(lp), {'color': U.get_contrast_color(lp.color)}]"
							:aria-label="repo_tile_accessible_label(lp, collection_categories[name].display_cat_name)"
							role="button"
							tabindex="0"
							@click="go_to_lp(lp)"
							@keydown="repo_tile_keydown($event, lp)"
						>
							<span>
								<k-icon small style="font-size:14px; margin-right:4px;" :style="`color:${U.get_contrast_color(lp.color)}`">{{lp.collection_icon()}}</k-icon><span v-html="lp_title(lp)"></span>
							</span>
						</div>
					</template>
				</div>

				<div
					v-if="default_repos.default_pd_repo_cat.lps.length > 0"
					class="k-repo-category"
					role="group"
					:aria-labelledby="repo_category_heading_id('default_pd')"
				>
					<div class="k-repo-category-label" :id="repo_category_heading_id('default_pd')" v-html="default_repos.default_pd_repo_cat.display_cat_name"></div>
					<div
						v-for="(lp, index) in default_repos.default_pd_repo_cat.lps"
						:key="'default_pd_' + index"
						v-show="collection_lps_to_show.find(x=>x.lp_id==lp.lp_id)"
						class="k-elevated k-main-collection-item text-left k-lp-tile k-repo-tile-button"
						:class="lp_css(lp)"
						:style="[lp_style(lp), {'color': U.get_contrast_color(lp.color)}]"
						:aria-label="repo_tile_accessible_label(lp, default_repos.default_pd_repo_cat.display_cat_name)"
						role="button"
						tabindex="0"
						@click="go_to_lp(lp)"
						@keydown="repo_tile_keydown($event, lp)"
						>
							<span>
								<k-icon small style="font-size:14px; margin-right:4px;" :style="`color:${U.get_contrast_color(lp.color)}`">{{lp.collection_icon()}}</k-icon><span v-html="lp_title(lp)"></span>
							</span>
						</div>
				</div>

				<div
					v-if="default_repos.default_repo_cat.lps.length > 0"
					class="k-repo-category"
					role="group"
					:aria-labelledby="repo_category_heading_id('default_repo')"
				>
					<div class="k-repo-category-label" :id="repo_category_heading_id('default_repo')" v-html="default_repos.default_repo_cat.display_cat_name"></div>
					<div
						v-for="(lp, index) in default_repos.default_repo_cat.lps"
						:key="'default_repo_' + index"
						v-show="collection_lps_to_show.find(x=>x.lp_id==lp.lp_id)"
						class="k-elevated k-main-collection-item text-left k-lp-tile k-repo-tile-button"
						:class="lp_css(lp)"
						:style="[lp_style(lp), {'color': U.get_contrast_color(lp.color)}]"
						:aria-label="repo_tile_accessible_label(lp, default_repos.default_repo_cat.display_cat_name)"
						role="button"
						tabindex="0"
						@click="go_to_lp(lp)"
						@keydown="repo_tile_keydown($event, lp)"
					>
						<span>
							<k-icon small style="font-size:14px; margin-right:4px;" :style="`color:${U.get_contrast_color(lp.color)}`">{{lp.collection_icon()}}</k-icon><span v-html="lp_title(lp)"></span>
						</span>
					</div>
				</div>
			</div>

			<div v-if="!small_screen&&!studentish_role&&!lti_chooser" class="d-flex align-center mt-4">
				<k-spacer/>
				<k-btn large class="k-tight-btn k-nocaps-btn mx-1" @click="enter_subscription_code"><k-icon class="mr-2 ml-6">{{collection_icon}}</k-icon><span class="mr-6">SUBSCRIBE to a Restricted Content Collection</span></k-btn>
				<div v-if="user_can_access_inactive_collections" class="ml-3">
					<k-checkbox class="mt-0 pt-0" label="Show inactive collections" v-model="show_inactive_collections" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"></k-checkbox>
				</div>
				<k-spacer/>
			</div>
		</div>
	</div>
</div></template>

<script>
import { mapState, mapGetters } from 'vuex'
import draggable from 'vuedraggable'

export default {
	components: { draggable},

	props: {
		lti_chooser: { type: Boolean, required: false, default() { return false }},
	},

	data() { return {
		initialized: false,
		search_string: '',
		search_terms: [],
		default_repos: {},
		drag_options: {
			animation: 200,
		},
		reorder_toggle: false,
		user_can_access_inactive_collections: false,
		category_trigger: 0,
	}},
	computed: {
		...mapState(['site_config', 'user_info', 'all_courses_loaded', 'all_courses', 'subdomain_data']),

		...mapGetters(['small_screen', 'signed_in', 'studentish_role']),

		custom_tab_content_description() { return this.site_config.customized_tabs?.resourcerepos?.description },
		collection_icon() { 
			// use this construct to get the right icon, which might be customized by site_config
			return new Learning_Progression({collection_type:'repo'}).collection_icon()
		},
		page_title() {
			// unfortunately we have two established ways of setting this...
			let s = this.site_config.resourcerepos_noun		// this defaults to 'Resource Repositories'
			if (!empty(this.site_config.customized_tabs?.resourcerepos?.title)) s = this.site_config.customized_tabs.resourcerepos.title
			if (this.site_config.show_pd == 'yes' && !this.studentish_role) {
				s += ' and PD Collections'
			}
			return s
		},

		last_collections_viewed: {
			get() { return this.$store.state.lst.last_collections_viewed },
			set(val) { this.$store.commit('lst_set', ['last_collections_viewed', val]) }
		},

		show_inactive_collections: {
			get() { return this.$store.state.lst.show_inactive_collections },
			set(val) { this.$store.commit('lst_set', ['show_inactive_collections', val]) }
		},

		collection_lps() {
			// this is just a trigger; category_trigger will never go below 0
			if (this.category_trigger < 0) return

			// side-effect: set user_can_access_inactive_collections
			this.user_can_access_inactive_collections = false
			let arr = this.all_courses.filter(lp => {
				// when we first create a new LP to be entered, its title will be false
				if (lp.title == '') return false

				// for inactive lp's, only show to people explicitly authorized as viewers (this includes admins)
				if (lp.active == 'no') {
					if (!lp.user_can_view_lp()) return false		// && !this.is_repo_admin
					this.user_can_access_inactive_collections = true

					// if we're not showing inactive collections, don't include at this point (after we've set user_can_access_inactive_collections)
					if (!this.show_inactive_collections) return false
				}
				
				// only include collection_types 'repo' and 'pd' here...
				if (!['repo', 'pd'].includes(lp.collection_type)) return false

				// and parents can only see repos (that are designed for viewing by them?)
				if (this.studentish_role) {
					if (lp.collection_type == 'pd') return false
				}

				return true
			})
			return arr
		},

		collection_lps_to_show() {
			// this is just a trigger; category_trigger will never go below 0
			if (this.category_trigger < 0) return

			let arr = this.collection_lps.filter(lp => {
				// if we have repositories_to_include, limit to those
				if (this.site_config.repositories_to_include && !this.site_config.repositories_to_include.includes(lp.course_code)) {
					// but *DO* include repositories updated after include_lps_updated_after (if set), even if they aren't in the list to include
					if (empty(this.site_config.include_lps_updated_after) || lp.updated_at < this.site_config.include_lps_updated_after) {
						return false
					}
				}

				// if all search_terms appear in the title, return true
				let tlc = lp.title.toLowerCase()
				for (let term of this.search_terms) {
					if (tlc.indexOf(term) == -1) return false
				}

				return true
			})

			arr.sort(this.category_lps_sort)

			return arr
		},

		collection_categories() {
			// this is just a trigger; category_trigger will never go below 0
			if (this.category_trigger < 0) return

			// MC: the categories are stored with the key={full_category_name}, full_category_name = 5_cat name,
			// 	   where the number represents the order of it and the values is lps, and the display name
			//     we do this so we can populate the category_order by simply sorting the keys
			let cats = {}
			let default_repos = {
				default_repo_cat: {
					display_cat_name: "Resource Repositories",
					repo_in_category: false,
					lps: [],
				},
				default_pd_repo_cat: {
					display_cat_name: "PD Repositories",
					pd_in_category: false,
					lps: [],
				},
			}

			// for (let collection of this.collection_lps_to_show) {
			for (let collection of this.collection_lps) {
				// don't return subdomain collections here (we deal with them separately, in subdomain_repos
				if (!empty(collection.subdomain)) continue

				if (collection.repo_category) {
					if (collection.collection_type == 'pd') default_repos.pd_in_category = true
					else default_repos.repo_in_category = true
					const display_cat_name = collection.repo_category.split('_').slice(1).join('_');
					if (cats && !(collection.repo_category in cats)) {
						cats[collection.repo_category] = {}
						cats[collection.repo_category].lps = []
						cats[collection.repo_category].display_cat_name = display_cat_name
					}
					cats[collection.repo_category].lps.push(collection)

				} else if (collection.collection_type == 'repo') {
					default_repos.default_repo_cat.lps.push(collection)

				} else {
					default_repos.default_pd_repo_cat.lps.push(collection)
				}
			}

			// sort each category
			for (let key in cats) {
				cats[key].lps.sort(this.category_lps_sort)
			}

			if (default_repos.pd_in_category) default_repos.default_pd_repo_cat.display_cat_name = "Other PD Repositories"
			if (default_repos.repo_in_category) default_repos.default_repo_cat.display_cat_name = "Other Repositories"

			default_repos.default_repo_cat.lps.sort(this.category_lps_sort)
			default_repos.default_pd_repo_cat.lps.sort(this.category_lps_sort)

			this.default_repos = default_repos

			return cats
		},

		category_order() {
			return Object.keys(this.collection_categories).sort((a,b)=>U.natural_sort(a, b))
		},

		is_repo_admin() {
			return vapp.has_admin_right('repo')
		},
		user_is_superuser() {
			return vapp.has_admin_right('su')
		},

		subdomain_repos() {
			if (!this.all_courses_loaded || empty(this.subdomain_data)) return []
			return this.collection_lps_to_show.filter(x=>x.subdomain == this.subdomain_data.subdomain)
		},

		show_subdomain_edit_tools() {
			return this.subdomain_data && vapp.has_admin_right('subdomain_admin')
		},

		show_subdomain_category() {
			if (this.subdomain_data) {
				if (this.subdomain_repos.length > 0 || this.show_subdomain_edit_tools) {
					return true
				}
			}
			return false
		},
	},

	watch: {
		search_string() {
			if (empty(this.search_string)) {
				this.search_terms = []
			} else {
				// convert string to lower case and split on spaces
				this.search_terms = $.trim(this.search_string).toLowerCase().split(/\s+/)
			}
		}
	},

	created() {
		if (!this.all_courses_loaded) {
			this.$store.dispatch('get_all_courses', 'initial').then(() => {
				this.$nextTick(() => {
					this.initialized = true
					this.trigger_category_update()
				})
			}).catch(() => {
				console.log('error in get_all_courses')
				this.back_to_classes()
			})
		} else {
			this.initialized = true
			this.trigger_category_update()
		}
		vapp.repo_index_component = this
	},
	
	methods: {
		go_to_favorites() {
			window.history.replaceState(null, '', '/repos')
			if (!this.signed_in) this.$store.commit('lst_set', ['unsigned_index_view_flavor', 'favorites'])
			else this.$store.commit('lst_set', ['repo_index_view_flavor', 'favorites'])
		},

		lp_css(lp) {
			// let s = vapp.color_from_number(lp.course_code)
			// let s = U.subject_tile_css(lp)
			let s = ''

			if (lp.title.length > 50) {
				s += ' k-lp-tile-extra-long-title'
			} else if (lp.title.length > 30) {
				s += ' k-lp-tile-long-title'
			}
			if (lp.active == 'no') {
				s += ' k-lp-tile-inactive'
			}

			return s
		},

		lp_style(lp) {
			return U.collection_color_style(lp)
		},

		lp_title(lp) {
			let s = ''
			// add <wbr> tags after slashes
			s += lp.title.replace(/\//g, '/<wbr>')
			if (lp.subscription_only) {
				s += '<div class="mt-1 text-center" style="font-size: 9px"><b>SUBSCRIPTION ONLY</b></div>'
			}
			return s
		},

		safe_id_fragment(val) {
			return String(val || '')
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, '-')
				.replace(/^-+|-+$/g, '')
		},

		repo_category_heading_id(name) {
			return `repo-category-heading-${this.safe_id_fragment(name)}`
		},

		repo_tile_accessible_label(lp, category_name='') {
			let base = `Open ${lp.title}`
			if (category_name) base += ` in ${category_name}`
			if (lp.active == 'no') base += ', inactive'
			return base
		},

		go_to_lp(lp) {
			this.$store.commit('set', ['last_lp_list', 'index'])
			this.$router.push({ path: lp.vue_route() })

			// in lti_chooser mode, emit event, which will show the collection
			if (this.lti_chooser) {
				this.$emit('go_to_collection', lp)
			}
		},

		repo_tile_keydown(event, lp) {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault()
				this.go_to_lp(lp)
			}
		},

		// this is called when the categories are reordered; this.category_order value will reflect the new order. here we have to check, and possibly change, the repo_category values of all lps
		category_order_changed() {
			let payload = {user_id: this.user_info.user_id, lps_to_update: []}

			// for each category...
			for (let i = 0; i < this.category_order.length; ++i) {
				// get old name and construct new name, which incorporates the sequence number at the start
				let old_repo_category = this.category_order[i]
				let new_repo_category = (100+i) + '_' + old_repo_category.replace(/^\d+_/, '')	// first category will be 100, second will be 101, etc.

				// if category name changes, we have to update all lps with the old category to have the new category
				if (old_repo_category != new_repo_category) {
					for (let lp of this.collection_lps) {
						if (lp.repo_category == old_repo_category) {
							this.$store.commit('set', [lp, 'repo_category', new_repo_category])
							// no need to change the sequence of item in the category

							payload.lps_to_update.push({course_code: lp.course_code, repo_category: new_repo_category})
						}
					}
				}
			}
			this.update_lp_categories(payload)

			this.trigger_category_update()
		},

		// this is called when an LP is dragged from one position to another. if the LP is moved within a category, it'll just be called once; if it's moved between categories, it'll be called twice: for the "to" cat and the "from" cat
		// in both cases, 'category_name' will be the name of the category, and 'new_order' will be the new list of LPs in the category
		repo_order_changed(category_name, new_order) {
			// console.log('repo_order_changed', category_name, new_order, $event)
			const new_order_course_codes = new_order.map(({course_code}) => course_code)

			let payload = {user_id: this.user_info.user_id, lps_to_update: []}
			for (let i = 0; i < new_order_course_codes.length; ++i) {
				let course_code = new_order_course_codes[i]
				let lp = this.all_courses.find(x=>x.course_code == course_code)
				if (!lp) { console.error('couldn’t find lp'); return; }	// shouldn't happen

				// when an lp is moved *into* a default category, this is called with the default category's list. in this case, we *remove* the repo_category, and set sequence to 0
				if (category_name == 'default_pd' || category_name == 'default_repo') {
					this.$store.commit('set', [lp, 'repo_category', ''])
					this.$store.commit('set', [lp, 'sequence', 0])

					payload.lps_to_update.push({course_code: course_code, repo_category: '', sequence: 0})

				} else {
					this.$store.commit('set', [lp, 'repo_category', category_name])
					this.$store.commit('set', [lp, 'sequence', i+1])	// lets make these 1-indexed

					payload.lps_to_update.push({course_code: course_code, repo_category: category_name, sequence: i})
				}
			}
			this.update_lp_categories(payload)
		},

		edit_category_title(cat) {
			console.log(cat)
			this.$prompt({
				title: 'Edit Category Title',
				text: 'Enter new category title:',
				initialValue: cat.display_cat_name,
				disableForEmptyValue: true,
				acceptText: 'Save',
				acceptIcon: 'fas fa-save',
			}).then(title => {
				title = $.trim(title)
				if (empty(title)) return

				// construct the new category title, then replace in all LPs
				let payload = {user_id: this.user_info.user_id, lps_to_update: []}
				let new_repo_category = cat.lps[0].repo_category.replace(/^(\d+_).*/, '$1') + title
				for (let lp of cat.lps) {
					this.$store.commit('set', [lp, 'repo_category', new_repo_category])
					payload.lps_to_update.push({course_code: lp.course_code, repo_category: new_repo_category})
				}

				this.update_lp_categories(payload)

				this.trigger_category_update()

			}).catch(n => {console.error(n)})
		},

		edit_lp_category_title(lp) {
			this.$prompt({
				title: 'New Category',
				text: 'Enter a new category title for this collection:',
				initialValue: '',
				disableForEmptyValue: true,
				acceptText: 'Save',
				acceptIcon: 'fas fa-save',
			}).then(title => {
				title = $.trim(title)
				if (empty(title)) return

				let new_repo_category = '999_' + title

				// if this category title already exists, use it (with its sequence number)
				for (let lp2 of this.collection_lps) {
					let rc_title = lp2.repo_category.replace(/^\d+_/, '')
					if (title == rc_title) {
						// this repo_category matches the entered one, so use it
						new_repo_category = lp2.repo_category
						break
					}
				}

				// replace repo category in this LP
				let payload = {user_id: this.user_info.user_id, lps_to_update: [{course_code: lp.course_code, repo_category: new_repo_category, sequence: 999}]}
				this.$store.commit('set', [lp, 'repo_category', new_repo_category])
				this.$store.commit('set', [lp, 'sequence', 999])

				this.update_lp_categories(payload)

				this.trigger_category_update()

			}).catch(n => {console.error(n)})
		},

		initiate_search() {
			// once we know standards-aligned assets are loaded, show search interface
			vapp.initiate_search({
				caller_component: this,
				dialog_title: 'Search ' + this.site_config.resourcerepos_noun,
				default_source: 'site',
			})
		},

		update_lp_categories(payload) {
			// note that we set category and/or sequence in the LP's $store representation before calling this; if the service fails (which it shouldn't), we'll alert an error
			// console.warn(payload); return
			U.ajax('update_lp_categories', payload, result=>{
				if (result.status != 'ok') {
					this.$alert('Error updating collection order: ' + result.status)
					vapp.ping()		// call ping to check if the session is expired
				}
			})
		},
		enter_subscription_code() {
			vapp.enter_subscription_code()
		},

		create_subdomain_repo() {
			this.$prompt({
				title: `New Agency Repository`,
				text: `Enter the title of the new repository you’d like to create for <nobr>${this.subdomain_data.agency_name}</nobr>:`,
				// initialValue: title,
				disableForEmptyValue: true,
				acceptText: 'Create Repository',
				acceptIconAfter: 'fas fa-circle-arrow-right',
				dialogMaxWidth: 540,
			}).then(title => {
				title = $.trim(title)
				if (empty(title)) return

				let new_lp = new Learning_Progression({
					title: title,
					agency_sanctioned: true, 
					lp_layout: 'tree',
					use_unit_numbers: false,
					units: [new LP_Unit()],
					active: 'no',
					collection_type: 'repo',
					subdomain: this.subdomain_data.subdomain,
					no_lock: true,	// bypass the checkout process
				})

				this.$store.dispatch('save_learning_progression', new_lp).then((res) => {
					// add new course to all_courses
					this.$store.commit('add_to_array', [this.all_courses, new_lp])
				})

			}).catch(n => {console.error(n)})
		},

		category_lps_sort(a,b) {
			if (a.sequence != b.sequence) return a.sequence - b.sequence
			// default sort by title
			return U.natural_sort(a.title, b.title)
		},

		trigger_category_update() {
			this.$nextTick(() => {
				++this.category_trigger
			})
		},
	}
}
</script>

<style lang="scss">

.k-main-collection-sub-collection {
	border-radius: 40px;
	padding: 4px;
	background-color: rgba(255, 255, 255, 0.5);
	margin-bottom: 16px;
	display: inline-block;	// make it shrink to fit whatever is inside of it
	min-width: 460px;
}

.k-main-collection-sub-collection-featured {
	background-color: rgba(255, 240, 200, 0.5);
	border: 0;
}

.k-main-collection--course-showing {
	margin: 0 !important;
	padding: 0px 10px !important;

	.k-lp-tile {
		width: 100px !important;
		height: 100px !important;
		opacity: 0.6;
		margin: 10px;
		background-color: #f8f8f8;
		-webkit-box-shadow: 0 0 0 0 !important;
		box-shadow: 0 0 0 0 !important;
		font-size: 12px !important;
		line-height: 14px !important;
		font-weight: normal !important;
	}

	.k-lp-tile--repos-showing, .k-lp-tile--pd-showing {
		font-weight: 900 !important;
		background-color: #eee;
		opacity: 1.0;
		-webkit-box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12) !important;
		box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12) !important;
	}

	.k-main-collection-sub-collection {
		-webkit-box-shadow: none !important;
		box-shadow: none !important;
		background-color: transparent;
		padding: 0;
		margin-bottom: 0;
		border: 0px;
	}
}

.k-repo-category {
	position: relative;
	border-radius: 40px;
	padding: 36px 12px 12px 12px;
	// background-color: rgba(255, 255, 255, 0.5);
	background-color: #ddd;
	margin: 0 10px 20px 10px;
	min-width: 240px;
	display: inline-flex;	// make it shrink to fit whatever is inside of it
	flex-wrap: wrap;
	flex-direction: row;
	align-items: center;
	justify-content: center;

	.k-main-collection-item {
		margin: 8px !important;
	}
}

.k-subdomain-repo-category {
	background-color: $v-pink-lighten-5;
}

.k-repo-category-label {
	position: absolute;
	width: 100%;
	text-align: center;
	top: 12px;
	left: 0;
	font-weight: bold;
	font-size: 16px;
	line-height: 20px;
	white-space: nowrap;
	// padding: 0 4px;	// this is redundant if white-space is nowrap
}

.k-repo-tile-button {
	outline: none;
}

.k-repo-tile-button:focus-visible {
	outline: 3px solid #ffffff;
	outline-offset: 3px;
	box-shadow: 0 0 0 2px #0d47a1, 0 0 0 5px #ffffff, 0 0 0 7px #0d47a1;
}

</style>
